"use client"

import { useState } from "react"
import { track } from "@vercel/analytics"
import { Check, ArrowRight } from "lucide-react"

interface FormData {
  name: string
  company: string
  contact: string
  message: string
}

export function ContactForm() {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    company: "",
    contact: "",
    message: "",
  })
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({})
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle")

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
  }

  const validate = () => {
    const next: Partial<Record<keyof FormData, string>> = {}
    if (!formData.name.trim()) next.name = "Name is required"
    if (!formData.contact.trim()) next.contact = "We need a way to reach you"
    if (!formData.message.trim()) next.message = "Tell us a little about it"
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    setStatus("sending")
    try {
      const res = await fetch("/api/contact-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      })
      if (!res.ok) throw new Error("Request failed")
      track("contact_submitted")
      setStatus("sent")
    } catch {
      setStatus("error")
    }
  }

  // Success state
  if (status === "sent") {
    return (
      <div className="border border-border bg-card p-8 md:p-12">
        <div className="mx-auto max-w-lg text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center border border-primary bg-primary/20">
            <Check className="h-8 w-8 text-primary" />
          </div>
          <h2 className="mb-4 font-sans text-2xl tracking-wide text-foreground">
            Message transmitted{formData.name ? `, ${formData.name.split(" ")[0]}` : ""}.
          </h2>
          <p className="font-mono text-base leading-relaxed text-muted-foreground">
            We{"'"}ll reach out on your channel. Usually within a day.
          </p>
        </div>
      </div>
    )
  }

  const inputClass = (field: keyof FormData) =>
    `w-full border bg-transparent px-4 py-3 font-mono text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none ${
      errors[field] ? "border-destructive" : "border-border"
    }`

  return (
    <div className="border border-border bg-card p-6 md:p-10">
      {/* Terminal-style header */}
      <div className="mb-8">
        <p className="mb-3 font-mono text-xs text-primary">guest@futovia:~$ ./chat --new</p>
        <h1 className="mb-2 font-sans text-2xl tracking-wide text-foreground md:text-3xl">Chat with us</h1>
        <p className="font-mono text-sm text-muted-foreground">
          No sales call. Tell us what you{"'"}re building and how to reach you.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 md:grid-cols-2">
          {/* Name */}
          <div>
            <label className="mb-2 block font-mono text-sm text-foreground">
              NAME <span className="text-primary">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className={inputClass("name")}
              placeholder="Thomas Anderson"
            />
            {errors.name && <p className="mt-1 font-mono text-sm text-destructive">{errors.name}</p>}
          </div>

          {/* Company (optional) */}
          <div>
            <label className="mb-2 block font-mono text-sm text-foreground">
              COMPANY <span className="text-muted-foreground/60">(optional)</span>
            </label>
            <input
              type="text"
              value={formData.company}
              onChange={(e) => handleChange("company", e.target.value)}
              className={inputClass("company")}
              placeholder="Metacortex"
            />
          </div>
        </div>

        {/* WhatsApp / iMessage number */}
        <div className="mt-6">
          <label className="mb-2 block font-mono text-sm text-foreground">
            WHATSAPP / IMESSAGE NUMBER <span className="text-primary">*</span>
          </label>
          <input
            type="tel"
            value={formData.contact}
            onChange={(e) => handleChange("contact", e.target.value)}
            className={inputClass("contact")}
            placeholder="+1 (312) 555-0173"
          />
          {errors.contact && <p className="mt-1 font-mono text-sm text-destructive">{errors.contact}</p>}
        </div>

        {/* Message */}
        <div className="mt-6">
          <label className="mb-2 block font-mono text-sm text-foreground">
            MESSAGE <span className="text-primary">*</span>
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => handleChange("message", e.target.value)}
            rows={4}
            className={inputClass("message")}
            placeholder="Show us how deep the rabbit hole goes..."
          />
          {errors.message && <p className="mt-1 font-mono text-sm text-destructive">{errors.message}</p>}
        </div>

        {status === "error" && (
          <p className="mt-6 font-mono text-sm text-destructive">
            Something broke on our end. Email us directly at{" "}
            <a href="mailto:hello@futovia.com" className="underline hover:text-primary">
              hello@futovia.com
            </a>
            .
          </p>
        )}

        <div className="mt-8 flex justify-end">
          <button
            type="submit"
            disabled={status === "sending"}
            className="group flex items-center gap-2 bg-foreground px-6 py-3 font-mono text-sm text-background transition-colors hover:bg-foreground/90 disabled:opacity-60"
          >
            {status === "sending" ? "Transmitting..." : "Transmit"}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </form>
    </div>
  )
}
